import type { ComponentPlacementLocation } from "@intelligems/ig-types/dtos/components";
import type {
  OnsiteUpsellRuleAuthoring,
  UpsellCondition,
} from "@intelligems/ig-types/schemas/visual-builder/onsite-upsell-rules";
import { DormantAnchorPlacementSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/DormantAnchorPlacementSection";
import { LegacyPlacementSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/LegacyPlacementSection";
import { PlacementSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/PlacementSection";
import { useIsAgenticLogicEnabled } from "@src/hooks/feature-flags/useIsAgenticLogicEnabled";
import {
  DEFAULT_PLACEMENT,
  placementForMode,
  placementMode,
} from "@src/store/slices/experienceBuilder/component/utils/onsiteUpsellModHandler";
import type {
  OnsiteUpsellComponent,
  PlacementMode,
} from "@src/store/slices/experienceBuilder/types";

type PlacementFields = Pick<
  OnsiteUpsellComponent,
  "location" | "anchorKey" | "selector" | "insertType" | "rules"
>;

// Conditions `refineOnsiteUpsellRules` forbids per location; pruned on switch.
const INVALID_ON: Record<ComponentPlacementLocation, Set<string>> = {
  pdp: new Set(["variants", "cartSubtotal", "cartItemCount"]),
  cart: new Set(["products", "productPrice"]),
};

export const UpsellPlacementSection = ({
  mod,
  errors,
  onChange,
}: {
  mod: PlacementFields;
  errors?: Record<string, string>;
  onChange: (changes: Partial<PlacementFields>) => void;
}) => {
  const agenticLogicEnabled = useIsAgenticLogicEnabled();

  const changeLocation = (location: ComponentPlacementLocation) => {
    if (location === mod.location) {
      return;
    }
    const invalid = INVALID_ON[location];
    const rules: OnsiteUpsellRuleAuthoring[] = mod.rules.map((rule) => ({
      ...rule,
      conditions: rule.conditions.filter(
        (c: UpsellCondition) => !invalid.has(c.type)
      ),
    }));
    onChange({
      location,
      rules,
      // An anchor keeps its key; only per-location selector defaults reset.
      ...(mod.anchorKey
        ? {}
        : {
            selector: DEFAULT_PLACEMENT[location].selector,
            insertType: DEFAULT_PLACEMENT[location].insertType,
          }),
    });
  };

  const changeMode = (mode: PlacementMode) => {
    if (mode === placementMode(mod)) {
      return;
    }
    onChange(placementForMode(mode, mod.location, mod));
  };

  // Engine org: full editor. Legacy org: read-only when anchored (only the
  // engine writes anchors), else the legacy editor.
  if (agenticLogicEnabled) {
    return (
      <PlacementSection
        location={mod.location}
        mode={placementMode(mod)}
        anchorKey={mod.anchorKey ?? ""}
        selector={mod.selector ?? ""}
        insertType={mod.insertType}
        anchorKeyError={Boolean(errors?.anchorKey)}
        selectorError={Boolean(errors?.selector)}
        onLocationChange={changeLocation}
        onModeChange={changeMode}
        onChange={onChange}
      />
    );
  }

  if (mod.anchorKey) {
    return (
      <DormantAnchorPlacementSection
        location={mod.location}
        anchorKey={mod.anchorKey}
      />
    );
  }

  return (
    <LegacyPlacementSection
      location={mod.location}
      selector={mod.selector ?? DEFAULT_PLACEMENT[mod.location].selector}
      insertType={mod.insertType ?? DEFAULT_PLACEMENT[mod.location].insertType}
      selectorError={Boolean(errors?.selector)}
      onLocationChange={changeLocation}
      onChange={onChange}
    />
  );
};
